import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Collapse } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useQuery } from '@apollo/client';
import isAdministrator from '../utilities/checkPrivileges';
import Spinner from '../components/Spinner';
import DdmSchemaForm, { pruneDdmPayload } from '../components/DdmSchemaForm.jsx';
import { GET_CONFIG_PROFILES } from '../queries/configProfilesQueries';
import {
  getDdmStatus,
  getDdmSchemaCatalog,
  getDdmSchema,
  getDeclarations,
  getDeclaration,
  putDeclaration,
  deleteDeclaration,
  getLegacyProfileUrl,
} from '../services/nanoDdmService.js';

const FAMILIES = [
  { id: 'configurations', label: 'Konfigurationen' },
  { id: 'activations', label: 'Aktivierungen' },
  { id: 'assets', label: 'Assets' },
];

const FEATURED = [
  'com.apple.configuration.legacy',
  'com.apple.configuration.legacy.interactive',
  'com.apple.configuration.security.certificate',
  'com.apple.configuration.security.identity',
  'com.apple.configuration.passcode.settings',
  'com.apple.configuration.safari.settings',
  'com.apple.configuration.app.managed',
  'com.apple.configuration.softwareupdate.enforcement.specific',
  'com.apple.activation.simple',
  'com.apple.asset.credential.certificate',
  'com.apple.asset.credential.identity',
  'com.apple.asset.credential.scep',
];

/** Werden pro Gerät automatisch vom MDM gesetzt — nicht manuell im Editor anlegen. */
const SYSTEM_MANAGED_TYPES = new Set([
  'com.apple.configuration.management.status-subscriptions',
]);

function defaultIdentifier(declarationType) {
  const slug = String(declarationType || 'declaration')
    .replace(/^com\.apple\.(configuration|activation|asset|management)\./, '')
    .replace(/[^a-zA-Z0-9]+/g, '.');
  const suffix = Date.now().toString(36);
  return `de.teilseite.${slug}.${suffix}`.slice(0, 64);
}

function supportedOsBadges(supportedOS) {
  if (!supportedOS || typeof supportedOS !== 'object') return [];
  return Object.entries(supportedOS)
    .filter(([, info]) => info && info.introduced && info.introduced !== 'n/a')
    .map(([os, info]) => `${os} ${info.introduced}+`);
}

function familyOfType(type) {
  if (String(type).includes('.activation.')) return 'activations';
  if (String(type).includes('.asset.')) return 'assets';
  return 'configurations';
}

function isSystemIdentifier(identifier) {
  return String(identifier || '').startsWith('xavier.');
}

function shortType(type) {
  return String(type || '').replace(/^com\.apple\.(configuration|activation|asset|management)\./, '');
}

function collectReferencedIdentifiers(value, out = new Set()) {
  if (Array.isArray(value)) {
    value.forEach((item) => collectReferencedIdentifiers(item, out));
    return out;
  }
  if (!value || typeof value !== 'object') return out;
  if (typeof value.Identifier === 'string' && value.Identifier.trim()) {
    out.add(value.Identifier.trim());
  }
  if (typeof value.identifier === 'string' && value.identifier.trim()) {
    out.add(value.identifier.trim());
  }
  Object.values(value).forEach((child) => collectReferencedIdentifiers(child, out));
  return out;
}

/**
 * Flache Hierarchie aus DDM-Referenzen:
 * Aktivierung → StandardConfigurations → in Configs referenzierte Assets.
 * Kein Graph-Layout — nur eine lesbare Outline.
 */
function buildDeclarationOutline(existing = []) {
  const byId = new Map();
  for (const item of existing) {
    const id = item.Identifier || item.identifier;
    if (id) byId.set(id, item);
  }

  const usedAsChild = new Set();
  const roots = [];

  const assetChild = (assetId) => {
    const asset = byId.get(assetId);
    return {
      id: assetId,
      kind: 'asset',
      type: asset?.Type || asset?.type || '',
      missing: !asset,
    };
  };

  const configNode = (configId) => {
    usedAsChild.add(configId);
    const config = byId.get(configId);
    const payload = config?.Payload || config?.payload || {};
    const refs = [...collectReferencedIdentifiers(payload)].filter((ref) => {
      if (ref === configId) return false;
      const target = byId.get(ref);
      return target && familyOfType(target.Type || target.type) === 'assets';
    });
    refs.forEach((ref) => usedAsChild.add(ref));
    return {
      id: configId,
      kind: 'configuration',
      type: config?.Type || config?.type || '',
      missing: !config,
      children: refs.map(assetChild),
    };
  };

  for (const item of existing) {
    const id = item.Identifier || item.identifier;
    const type = item.Type || item.type;
    if (!id || familyOfType(type) !== 'activations') continue;
    if (isSystemIdentifier(id)) continue;
    const configs = item.Payload?.StandardConfigurations || item.payload?.StandardConfigurations || [];
    const children = (Array.isArray(configs) ? configs : [])
      .map((entry) => (typeof entry === 'string' ? entry : entry?.Identifier || entry?.identifier))
      .filter(Boolean)
      .map(configNode);
    roots.push({
      id,
      kind: 'activation',
      type,
      missing: false,
      children,
    });
  }

  for (const item of existing) {
    const id = item.Identifier || item.identifier;
    const type = item.Type || item.type;
    if (!id || usedAsChild.has(id) || isSystemIdentifier(id)) continue;
    const fam = familyOfType(type);
    if (fam === 'activations') continue;
    if (fam === 'configurations') {
      roots.push(configNode(id));
    } else if (fam === 'assets') {
      roots.push(assetChild(id));
    }
  }

  return roots;
}

function OutlineNode({ node, depth = 0, onSelect, onDelete, deleting }) {
  const system = isSystemIdentifier(node.id);
  return (
    <li className="ddm-outline-node">
      <div className="d-flex align-items-start gap-2 py-1" style={{ paddingLeft: depth * 16 }}>
        <span className={`badge text-bg-${node.kind === 'activation' ? 'primary' : node.kind === 'configuration' ? 'success' : 'secondary'} flex-shrink-0`}>
          {node.kind === 'activation' ? 'Akt.' : node.kind === 'configuration' ? 'Konf.' : 'Asset'}
        </span>
        <span
          role="button"
          tabIndex={node.missing ? -1 : 0}
          className={`ddm-outline-select flex-grow-1 text-break${node.missing ? ' is-disabled' : ''}`}
          onClick={() => {
            if (!node.missing) onSelect(node.id);
          }}
          onKeyDown={(e) => {
            if (node.missing) return;
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onSelect(node.id);
            }
          }}
          title={node.type || undefined}
          aria-disabled={node.missing || undefined}
        >
          <code>{node.id}</code>
          {node.missing ? <span className="small text-danger ms-2">fehlt</span> : null}
          {node.type ? <div className="small text-muted">{shortType(node.type)}</div> : null}
        </span>
        {!node.missing ? (
          <button
            type="button"
            className="btn btn-sm btn-outline-danger flex-shrink-0"
            disabled={system || deleting === node.id}
            onClick={() => onDelete(node.id)}
          >
            {deleting === node.id ? '…' : 'Löschen'}
          </button>
        ) : null}
      </div>
      {node.children?.length ? (
        <ul className="list-unstyled mb-0">
          {node.children.map((child) => (
            <OutlineNode
              key={`${node.id}->${child.id}`}
              node={child}
              depth={depth + 1}
              onSelect={onSelect}
              onDelete={onDelete}
              deleting={deleting}
            />
          ))}
        </ul>
      ) : null}
    </li>
  );
}

export default function DdmDeclarations() {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const [status, setStatus] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [schema, setSchema] = useState(null);
  const [family, setFamily] = useState('configurations');
  const [selectedType, setSelectedType] = useState('');
  const [identifier, setIdentifier] = useState('');
  const [payload, setPayload] = useState({});
  const [existing, setExisting] = useState([]);
  const [showJson, setShowJson] = useState(false);
  const [showSystem, setShowSystem] = useState(false);
  const [typeQuery, setTypeQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState('');
  const [profileUrls, setProfileUrls] = useState({});
  const pendingLoad = useRef(null);

  const { data: profilesData } = useQuery(GET_CONFIG_PROFILES, { skip: !isAdministrator() });
  const profiles = profilesData?.configProfiles || [];

  useEffect(() => {
    if (!user) navigate('/login');
  }, [user, navigate]);

  useEffect(() => {
    if (!isAdministrator()) return;
    let cancelled = false;
    (async () => {
      try {
        const [ddmStatus, catalogData] = await Promise.all([
          getDdmStatus(),
          getDdmSchemaCatalog({ family: 'all' }),
        ]);
        if (cancelled) return;
        setStatus(ddmStatus);
        setCatalog(catalogData);
        const first = (catalogData?.declarations || []).find((item) => item.family === 'configurations' && FEATURED.includes(item.type))
          || (catalogData?.declarations || []).find((item) => item.family === 'configurations');
        if (first) setSelectedType(first.type);
      } catch (err) {
        toast.error(err.response?.data?.message || err.message || 'DDM-Schemas konnten nicht geladen werden.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!selectedType) {
      setSchema(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const nextSchema = await getDdmSchema(selectedType);
        if (cancelled) return;
        setSchema(nextSchema);
        if (pendingLoad.current && (pendingLoad.current.Type || pendingLoad.current.type) === selectedType) {
          setIdentifier(pendingLoad.current.Identifier || pendingLoad.current.identifier || '');
          setPayload(pendingLoad.current.Payload || pendingLoad.current.payload || {});
          pendingLoad.current = null;
        } else {
          setIdentifier(defaultIdentifier(selectedType));
          setPayload({});
        }
      } catch (err) {
        toast.error(err.response?.data?.message || 'Schema nicht gefunden.');
      }
    })();
    return () => { cancelled = true; };
  }, [selectedType]);

  const reloadExisting = useCallback(async () => {
    if (!status?.available) {
      setExisting([]);
      return;
    }
    try {
      const listed = await getDeclarations({ detail: true });
      setExisting(listed?.declarations || []);
    } catch {
      setExisting([]);
    }
  }, [status?.available]);

  useEffect(() => {
    reloadExisting();
  }, [reloadExisting, saving]);

  const types = useMemo(() => {
    const rows = (catalog?.declarations || [])
      .filter((item) => item.family === family)
      .filter((item) => !SYSTEM_MANAGED_TYPES.has(item.type));
    const q = typeQuery.trim().toLowerCase();
    const filtered = q
      ? rows.filter((item) => `${item.title} ${item.type} ${item.filename}`.toLowerCase().includes(q))
      : rows;
    return [...filtered].sort((a, b) => {
      const af = FEATURED.includes(a.type) ? 0 : 1;
      const bf = FEATURED.includes(b.type) ? 0 : 1;
      if (af !== bf) return af - bf;
      return a.title.localeCompare(b.title, 'de');
    });
  }, [catalog, family, typeQuery]);

  const visibleExisting = useMemo(() => {
    return (existing || []).filter((item) => {
      const id = item.Identifier || item.identifier;
      if (!showSystem && isSystemIdentifier(id)) return false;
      return familyOfType(item.Type || item.type) === family;
    });
  }, [existing, family, showSystem]);

  const outline = useMemo(() => {
    const scope = showSystem
      ? (existing || [])
      : (existing || []).filter((item) => !isSystemIdentifier(item.Identifier || item.identifier));
    const full = buildDeclarationOutline(scope);

    if (family === 'activations') {
      return full.filter((node) => node.kind === 'activation');
    }

    if (family === 'configurations') {
      const byId = new Map();
      const collect = (nodes = []) => {
        for (const node of nodes) {
          if (node.kind === 'configuration' && !byId.has(node.id)) byId.set(node.id, node);
          if (node.children?.length) collect(node.children);
        }
      };
      collect(full);
      return [...byId.values()];
    }

    return scope
      .filter((item) => familyOfType(item.Type || item.type) === 'assets')
      .map((item) => ({
        id: item.Identifier || item.identifier,
        kind: 'asset',
        type: item.Type || item.type,
        missing: false,
        children: [],
      }));
  }, [existing, family, showSystem]);

  const configurations = useMemo(
    () => (existing || []).filter((item) => familyOfType(item.Type || item.type) === 'configurations' && !isSystemIdentifier(item.Identifier || item.identifier)),
    [existing]
  );
  const assets = useMemo(
    () => (existing || []).filter((item) => familyOfType(item.Type || item.type) === 'assets' && !isSystemIdentifier(item.Identifier || item.identifier)),
    [existing]
  );

  const declarationJson = useMemo(() => ({
    Type: selectedType,
    Identifier: identifier.trim(),
    Payload: pruneDdmPayload(payload) || {},
  }), [selectedType, identifier, payload]);

  const notes = (schema?.notes || []).map((note) => note.content).filter(Boolean);

  const loadExisting = async (id) => {
    try {
      const decl = await getDeclaration(id);
      const type = decl.Type || decl.type;
      pendingLoad.current = decl;
      const nextFamily = familyOfType(type);
      if (nextFamily !== family) setFamily(nextFamily);
      if (type && type !== selectedType) {
        setSelectedType(type);
      } else {
        setIdentifier(decl.Identifier || id);
        setPayload(decl.Payload || {});
        pendingLoad.current = null;
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Declaration konnte nicht geladen werden.');
    }
  };

  const handleDelete = async (id) => {
    if (isSystemIdentifier(id)) {
      toast.error('System-Deklarationen können nicht gelöscht werden.');
      return;
    }
    if (!window.confirm(`Deklaration ${id} wirklich löschen? Sie wird auch aus Gruppenzuweisungen entfernt.`)) return;
    setDeleting(id);
    try {
      await deleteDeclaration(id);
      toast.success('Deklaration gelöscht.');
      if (identifier === id) {
        setIdentifier(defaultIdentifier(selectedType));
        setPayload({});
      }
      await reloadExisting();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Löschen fehlgeschlagen.');
    } finally {
      setDeleting('');
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!declarationJson.Identifier) {
      toast.error('Identifier ist erforderlich.');
      return;
    }
    if (!status?.available) {
      toast.error('NanoHUB DDM ist nicht konfiguriert.');
      return;
    }
    const requiredMissing = (schema?.payloadkeys || [])
      .filter((key) => key.presence === 'required')
      .filter((key) => pruneDdmPayload(payload)?.[key.key] === undefined);
    if (requiredMissing.length) {
      toast.error(`Pflichtfelder fehlen: ${requiredMissing.map((k) => k.title || k.key).join(', ')}`);
      return;
    }
    setSaving(true);
    try {
      await putDeclaration(declarationJson);
      toast.success('Declaration in KMFDDM gespeichert.');
      await reloadExisting();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Speichern fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  };

  if (!isAdministrator()) {
    return <p className="text-muted my-4">Nur Administratoren können DDM-Deklarationen bearbeiten.</p>;
  }
  if (loading) return <Spinner />;

  return (
    <div className="container py-3">
      <h2 className="mb-2">DDM-Deklarationen</h2>
      <p className="text-muted">
        Formulare kommen aus Apples Schema-Repository
        {' '}
        <code>apple/device-management</code>
        {catalog?.ref ? ` (${catalog.ref})` : ''}.
        Status-Abonnements (<code>management.status-subscriptions</code>) setzt das MDM automatisch
        pro Gerät — bitte nicht manuell anlegen.
        WLAN, Single App Mode, Layout und Blacklist liegen als klassische Profile vor und werden über
        {' '}
        <strong>Legacy-Profil</strong>
        {' '}
        als DDM ausgespielt. Zertifikate sind native Assets/Konfigurationen.
        Aktivierungen steuern, wann eine Konfiguration gilt.
      </p>

      {status && !status.available && (
        <div className="alert alert-warning">
          NanoHUB DDM ist nicht konfiguriert. Das Formular funktioniert, Speichern in KMFDDM aber nicht.
        </div>
      )}

      <ul className="nav nav-tabs mb-3">
        {FAMILIES.map((item) => (
          <li className="nav-item" key={item.id}>
            <button
              type="button"
              className={`nav-link ${family === item.id ? 'active' : ''}`}
              onClick={() => {
                setFamily(item.id);
                const next = (catalog?.declarations || []).find((row) => row.family === item.id);
                if (next) setSelectedType(next.type);
              }}
            >
              {item.label}
            </button>
          </li>
        ))}
      </ul>

      <div className="card mb-4">
        <div className="card-body py-3">
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
            <div>
              <div className="fw-semibold">Bereits in KMFDDM</div>
              <div className="small text-muted">
                {family === 'activations'
                  ? 'Aktivierungen mit referenzierten Konfigurationen und Assets.'
                  : family === 'configurations'
                    ? 'Konfigurationen inkl. referenzierter Assets (auch wenn sie in Aktivierungen hängen).'
                    : 'Asset-Deklarationen dieser Rubrik.'}
              </div>
            </div>
            <div className="form-check mb-0">
              <input
                id="show-system-ddm"
                type="checkbox"
                className="form-check-input"
                checked={showSystem}
                onChange={(e) => setShowSystem(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="show-system-ddm">System-Deklarationen zeigen</label>
            </div>
          </div>
          {outline.length === 0 ? (
            <p className="small text-muted mb-0">Keine gespeicherten Deklarationen in dieser Rubrik.</p>
          ) : (
            <div className="ddm-existing-list border rounded px-2">
              <ul className="list-unstyled mb-0">
                {outline.map((node) => (
                  <OutlineNode
                    key={node.id}
                    node={node}
                    onSelect={loadExisting}
                    onDelete={handleDelete}
                    deleting={deleting}
                  />
                ))}
              </ul>
            </div>
          )}
          {visibleExisting.length > 0 && family !== 'assets' ? (
            <p className="small text-muted mb-0 mt-2">
              {visibleExisting.length} Einträge in dieser Rubrik
              {outline.length !== visibleExisting.length ? ` · ${outline.length} Wurzelknoten in der Übersicht` : ''}
            </p>
          ) : null}
        </div>
      </div>

      <div className="row g-4">
        <div className="col-lg-4">
          <label className="form-label">Deklarationstyp</label>
          <input
            className="form-control form-control-sm mb-2"
            placeholder="Typ suchen…"
            value={typeQuery}
            onChange={(e) => setTypeQuery(e.target.value)}
          />
          <select
            className="form-select mb-3"
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
          >
            {types.map((item) => (
              <option key={item.type} value={item.type}>
                {FEATURED.includes(item.type) ? '★ ' : ''}{item.title}
              </option>
            ))}
          </select>

          {schema && (
            <>
              <p>{schema.description}</p>
              <p className="small text-muted mb-2">
                {supportedOsBadges(schema.supportedOS).join(' · ')}
              </p>
              <code className="small d-block mb-3">{schema.type}</code>
            </>
          )}
        </div>

        <div className="col-lg-8">
          <form onSubmit={handleSave}>
            <div className="mb-3">
              <label className="form-label" htmlFor="ddm-identifier">Identifier *</label>
              <input
                id="ddm-identifier"
                className="form-control"
                value={identifier}
                maxLength={64}
                required
                onChange={(e) => setIdentifier(e.target.value)}
              />
              <div className="form-text">Eindeutig, maximal 64 Zeichen. KMFDDM setzt ServerToken selbst.</div>
            </div>

            <DdmSchemaForm
              schema={schema}
              payload={payload}
              onChange={setPayload}
              options={{
                configurations,
                assets,
                profiles,
                profileUrls,
                loadProfileUrl: async (id) => {
                  const data = await getLegacyProfileUrl(id);
                  const url = data.url;
                  setProfileUrls((prev) => ({ ...prev, [id]: url }));
                  return url;
                },
              }}
            />

            <div className="mb-3">
              <button
                type="button"
                className="btn btn-link px-0"
                onClick={() => setShowJson((open) => !open)}
              >
                {showJson ? 'JSON ausblenden' : 'JSON einblenden'}
              </button>
              <Collapse in={showJson}>
                <pre className="bg-light border rounded p-3 small">{JSON.stringify(declarationJson, null, 2)}</pre>
              </Collapse>
            </div>

            {notes.length > 0 && (
              <div className="alert alert-secondary small">
                {notes.map((note, index) => (
                  <p key={index} className="mb-2" style={{ whiteSpace: 'pre-wrap' }}>{note}</p>
                ))}
              </div>
            )}

            <div className="d-flex flex-wrap gap-2">
              <button type="submit" className="btn btn-primary" disabled={saving || !schema}>
                {saving ? 'Speichern…' : 'In KMFDDM speichern'}
              </button>
              {identifier && !isSystemIdentifier(identifier) && existing.some((item) => (item.Identifier || item.identifier) === identifier) && (
                <button
                  type="button"
                  className="btn btn-outline-danger"
                  disabled={Boolean(deleting)}
                  onClick={() => handleDelete(identifier)}
                >
                  Diese Deklaration löschen
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
